import coreStore from '@/stores/core';
import {
  APP_BOT_TYPE,
  FROM,
  ALL_BOT_TYPE,
  HotQuestionCategoryDisplayType,
  INTENT_HOT_QUESTION_DISPLAY_TYPE,
  NewHotQuestionDisplayType,
} from '@/utils/constants';
import { queryUAParser } from './queryUAParser';
import reporter from './report';
import { isBank, isDriver, isDriverOrFood, isShopee } from '@/config/core';
import { ShopeeEntryPoint } from '@/config/constants';
import { isDriverAndFoodUIOptimisation } from './experiment/getDriverAndFoodUIOptimisationExperiment';
import { HQ_UI_OPTIMISATION_GROUP } from './experiment/getHqUIOptimisation';
import { isAutoPopup, isOldUI } from './experiment/getArticleUIUXExperimen';
import { IntentSource } from '@/types/intent';

export enum FEATURE_TOGGLE_TYPE {
  AUTO_SUGGESTION = 'autoSuggestion',
  INTENT_PREDICTION_SUGGESTION = 'intent_prediction_suggestion',
  OVERFLOW_MANAGEMENT = 'overflowManagement',
  CHATBOT_CSAT_WITH_RESOLVED = 'chatbotCsatWithResolve',
  CHATBOT_CSAT_RATING_CHANGEABLE = 'chatbotCsatRatingChangeable',
  CHATBOT_RN_CSAT_SHOW = 'chatbotRnCsatShow',
  CHATBOT_CSAT_SHOW = 'chatbotCsatShow',
  UI_OPTIMISATION_PART_3_ACCORDION = 'uiOptimisationPart3Accordion',
  UI_OPTIMISATION_PART_3_BUTTON = 'uiOptimisationPart3Button',
  UI_OPTIMISATION_PART_3_TABLE = 'uiOptimisationPart3Table',
  UI_OPTIMISATION_HOT_QUESTION_CATEGORY = 'uiOptimisationHotQuestionCategory',
  UI_OPTIMISATION_HOT_QUESTION_CATEGORY_LANDING_PAGE = 'uiOptimisationHotQuestionCategoryLandingPage',
  UI_OPTIMISATION_INTENT_HOT_QUESTION = 'uiOptimisationIntentHotQuestion',
  UI_OPTIMISATION_ARTICLE = 'uiOptimisationArticle',
  UI_OPTIMISATION_ARTICLE_AUTO_POPUP = 'uiOptimisationArticleAutoPopup',
  UI_OPTIMISATION_MINIMISE_CATEGORY_HOT_QUESTION = 'uiOptimisationMinimiseCategoryHotQuestion'
}

export interface TogglesConfigs {
  // Support functions and boolean types
  [FEATURE_TOGGLE_TYPE.AUTO_SUGGESTION]: () => boolean;
  [FEATURE_TOGGLE_TYPE.INTENT_PREDICTION_SUGGESTION]: () => boolean;
  [FEATURE_TOGGLE_TYPE.OVERFLOW_MANAGEMENT]: () => boolean;
  [FEATURE_TOGGLE_TYPE.CHATBOT_CSAT_WITH_RESOLVED]: () => boolean;
  [FEATURE_TOGGLE_TYPE.CHATBOT_CSAT_RATING_CHANGEABLE]: () => boolean;
  [FEATURE_TOGGLE_TYPE.CHATBOT_RN_CSAT_SHOW]: () => boolean;
  [FEATURE_TOGGLE_TYPE.CHATBOT_CSAT_SHOW]: () => boolean;
  [FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_PART_3_ACCORDION]: () => boolean;
  [FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_PART_3_BUTTON]: () => boolean;
  [FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_PART_3_TABLE]: () => boolean;
  [FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_ARTICLE]: () => boolean;
  [FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_ARTICLE_AUTO_POPUP]: () => boolean;
  [FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_HOT_QUESTION_CATEGORY]: (displayType: HotQuestionCategoryDisplayType) => boolean;
  [FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_HOT_QUESTION_CATEGORY_LANDING_PAGE]: (
    displayType: HotQuestionCategoryDisplayType
  ) => boolean;
  [FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_INTENT_HOT_QUESTION]: (displayType: INTENT_HOT_QUESTION_DISPLAY_TYPE) => boolean;
  [FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_MINIMISE_CATEGORY_HOT_QUESTION]: (displayType: NewHotQuestionDisplayType, source: number) => boolean;
}

const IN_AB_TEST = 'test'; // 命中实验

const MINIMISE_CATEGORY_GROUPS = ['other_frequently_asked', 'smaller_tab'];

export const featureToggle = {
  _toggles: {
    [FEATURE_TOGGLE_TYPE.AUTO_SUGGESTION]() {
      const { appType } = coreStore.safeGetStore('initStore');
      // seller bot暂时关闭typing suggestion
      // bank不支持typing suggestion
      return appType !== APP_BOT_TYPE.SELLER_BOT && !isBank();
    },

    [FEATURE_TOGGLE_TYPE.INTENT_PREDICTION_SUGGESTION]() {
      const newABTestingConfigs = coreStore.safeGetStore('userStore').newABTestingConfigs as Record<string, unknown>;
      const { appType } = coreStore.safeGetStore('initStore');
      return (
        newABTestingConfigs.suggestion_prediction === 'suggestion_prediction'
        && appType === APP_BOT_TYPE.INTENT_BASE_BOT
        && isShopee()
      );
    },

    [FEATURE_TOGGLE_TYPE.OVERFLOW_MANAGEMENT]() {
      const { from } = coreStore.safeGetStore('initStore');
      const abTestingConfigs = coreStore.safeGetStore('userStore').abTestingConfigs as Record<string, unknown>;

      return (
        abTestingConfigs['bot.overflowmanagement_test'] === 'OverflowManagement'
        && from !== FROM.ShopeeFoodHC
        && from !== FROM.Mitra
      );
    },

    [FEATURE_TOGGLE_TYPE.CHATBOT_CSAT_WITH_RESOLVED]() {
      const abTestingConfigs = coreStore.safeGetStore('userStore').abTestingConfigs as Record<string, unknown>;
      return abTestingConfigs['bot.service_csat'] === 'group_2_with_resolved_question';
    },

    [FEATURE_TOGGLE_TYPE.CHATBOT_CSAT_RATING_CHANGEABLE]() {
      const newABTestingConfigs = coreStore.safeGetStore('userStore').newABTestingConfigs as Record<string, unknown>;
      return newABTestingConfigs.service_csat_update_ui === 'test';
    },

    [FEATURE_TOGGLE_TYPE.CHATBOT_CSAT_SHOW]() {
      const newABTestingConfigs = coreStore.safeGetStore('userStore').newABTestingConfigs as Record<string, unknown>;
      const { appType, from } = coreStore.safeGetStore('initStore');
      const bots = [APP_BOT_TYPE.INTENT_BASE_BOT, APP_BOT_TYPE.SOLUTION_BASE_BOT];
      const regions = ['BR', 'ID', 'MY', 'PH', 'SG', 'TH', 'TW', 'VN', 'CO', 'CL', 'MX'];
      const entryPoints = [
        ShopeeEntryPoint.mePage,
        ShopeeEntryPoint.helpCentre,
        ShopeeEntryPoint.shippingPage,
        ShopeeEntryPoint.RRShippingPage,
        ShopeeEntryPoint.OrderDetailPage,
        ShopeeEntryPoint.RRDetailsPage,
        ShopeeEntryPoint.ShopeeHomePage,
        ShopeeEntryPoint.sellingPage,
        ShopeeEntryPoint.mePagePremium,
        ShopeeEntryPoint.helpCentrePremium,
        ShopeeEntryPoint.RRDetailsPagePremium,
        ShopeeEntryPoint.OrderDetailPagePremium,
      ];
      const currentEntryPoint = ShopeeEntryPoint[from as keyof typeof ShopeeEntryPoint] ?? ShopeeEntryPoint.general;

      // 旧 csat 触发条件
      const rnver = queryUAParser('rnver');
      const { showChatbotCsat, csatRnBundleVersion } = coreStore.safeGetStore('initStore');
      const { interactedWithBot } = coreStore.safeGetStore('sessionStore');
      const { onceInQueue } = coreStore.safeGetStore('agentStore');
      const { sessionId } = coreStore.safeGetStore('initStore');
      const abTestingConfigs = coreStore.safeGetStore('userStore').abTestingConfigs as Record<string, unknown>;
      const resolvedConfig = ['group_1_no_resolved_question', 'group_2_with_resolved_question'];
      const isRnChatbotCastVersion = Number(rnver) >= Number(csatRnBundleVersion);

      console.log(
        'newABTestingConfigs.service_csat_within_chatbot === "scsat_within_chatbot"',
        newABTestingConfigs.service_csat_within_chatbot === 'scsat_within_chatbot',
      );
      console.log('regions.includes(COUNTRY)', regions.includes(COUNTRY.toUpperCase()), COUNTRY.toUpperCase());
      console.log('entryPoints.includes(currentEntryPoint)', entryPoints.includes(currentEntryPoint));
      console.log('isShopee()', isShopee());

      reporter.apiLogBeforeUnload(
        `Service CSAT SessionId:${sessionId};rnver:${rnver};abTestingConfigs:${
          abTestingConfigs['bot.service_csat']
        };onceInQueue:${onceInQueue};interactedWithBot:${interactedWithBot};appversion:${window.getAppVersion()};isDriver:${isDriver()}`,
        { level: 'debug' },
      );

      return (
        newABTestingConfigs.service_csat_within_chatbot === 'scsat_within_chatbot'
        // && bots.includes(appType)
        // bots.includes(appType)
        && resolvedConfig.includes(abTestingConfigs['bot.service_csat'] as string)
        && showChatbotCsat
        // && isRnChatbotCastVersion // TODO 暂时注释
        && !onceInQueue
        && interactedWithBot
        && regions.includes(COUNTRY.toUpperCase())
        && entryPoints.includes(currentEntryPoint)
        && isShopee()
      );
    },

    [FEATURE_TOGGLE_TYPE.CHATBOT_RN_CSAT_SHOW]() {
      const rnver = queryUAParser('rnver');
      const { showChatbotCsat, csatRnBundleVersion } = coreStore.safeGetStore('initStore');
      const { interactedWithBot } = coreStore.safeGetStore('sessionStore');
      const { onceInQueue } = coreStore.safeGetStore('agentStore');
      const { sessionId } = coreStore.safeGetStore('initStore');
      const abTestingConfigs = coreStore.safeGetStore('userStore').abTestingConfigs as Record<string, unknown>;
      const resolvedConfig = ['group_1_no_resolved_question', 'group_2_with_resolved_question'];
      const isRnChatbotCastVersion = Number(rnver) >= Number(csatRnBundleVersion);

      console.log(
        'rnver',
        rnver,
        'resolvedConfig',
        resolvedConfig.includes(abTestingConfigs['bot.service_csat'] as string),
        'showChatbotCsat',
        showChatbotCsat,
        'isRnChatbotCastVersion',
        isRnChatbotCastVersion,
        'onceInQueue',
        !onceInQueue,
        'interactedWithBot',
        interactedWithBot,
      );

      reporter.apiLogBeforeUnload(
        `Service CSAT SessionId:${sessionId};rnver:${rnver};abTestingConfigs:${
          abTestingConfigs['bot.service_csat']
        };onceInQueue:${onceInQueue};interactedWithBot:${interactedWithBot};appversion:${window.getAppVersion()};isDriver:${isDriver()}`,
        { level: 'debug' },
      );

      if (isDriver() || isBank()) {
        return showChatbotCsat && !onceInQueue && interactedWithBot;
      }

      return (
        resolvedConfig.includes(abTestingConfigs['bot.service_csat'] as string)
        && showChatbotCsat
        && isRnChatbotCastVersion
        && !onceInQueue
        && interactedWithBot
      );
    },

    [FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_PART_3_ACCORDION]() {
      const abTestingConfigs = coreStore.safeGetStore('userStore').newABTestingConfigs as Record<string, unknown>;
      const initStore = coreStore.safeGetStore('initStore');
      const isShopeeBot = initStore?.botType === ALL_BOT_TYPE.INTENT_BASE_BOT;
      const isDriverBot = initStore?.botType === ALL_BOT_TYPE.SHOPEE_FOOD_BOT;
      return abTestingConfigs?.accordion_ui === IN_AB_TEST && (isShopeeBot || isDriverBot || isBank());
    },

    [FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_PART_3_BUTTON]() {
      const abTestingConfigs = coreStore.safeGetStore('userStore').newABTestingConfigs as Record<string, unknown>;
      const initStore = coreStore.safeGetStore('initStore');
      const isShopeeBot = initStore?.botType === ALL_BOT_TYPE.INTENT_BASE_BOT;
      const isDriverBot = initStore?.botType === ALL_BOT_TYPE.SHOPEE_FOOD_BOT;
      return abTestingConfigs?.button_ui === IN_AB_TEST && (isShopeeBot || isDriverBot || isBank());
    },

    [FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_PART_3_TABLE]() {
      const abTestingConfigs = coreStore.safeGetStore('userStore').newABTestingConfigs as Record<string, unknown>;
      const initStore = coreStore.safeGetStore('initStore');
      const isShopeeBot = initStore?.botType === ALL_BOT_TYPE.INTENT_BASE_BOT;
      const isDriverBot = initStore?.botType === ALL_BOT_TYPE.SHOPEE_FOOD_BOT;
      return abTestingConfigs?.table_ui === IN_AB_TEST && (isShopeeBot || isDriverBot || isBank());
    },

    [FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_HOT_QUESTION_CATEGORY](displayType: HotQuestionCategoryDisplayType) {
      return [HotQuestionCategoryDisplayType.OtherFrequentlyAsked, HotQuestionCategoryDisplayType.SmallerTab].includes(
        displayType,
      );
    },

    [FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_HOT_QUESTION_CATEGORY_LANDING_PAGE](
      displayType: HotQuestionCategoryDisplayType,
    ) {
      return displayType !== HotQuestionCategoryDisplayType.OtherFrequentlyAsked;
    },

    [FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_INTENT_HOT_QUESTION](displayType: INTENT_HOT_QUESTION_DISPLAY_TYPE) {
      const initStore = coreStore.safeGetStore('initStore');
      const isBuyerBot = initStore?.botType === ALL_BOT_TYPE.INTENT_BASE_BOT;

      const isChangeQuestionsOrViewMore = [
        INTENT_HOT_QUESTION_DISPLAY_TYPE.GROUP1,
        INTENT_HOT_QUESTION_DISPLAY_TYPE.GROUP2,
      ].includes(displayType);
      const newABTestingConfigs = coreStore.safeGetStore('userStore').newABTestingConfigs || {};
      const { hq_ui_optimisation, minimise_category_general_entry_point } = newABTestingConfigs;
      const isEdd = hq_ui_optimisation === HQ_UI_OPTIMISATION_GROUP.EDD;

      return (
        isBuyerBot
        && isChangeQuestionsOrViewMore
        && isEdd
        && MINIMISE_CATEGORY_GROUPS.includes(minimise_category_general_entry_point)
      );
    },

    [FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_ARTICLE]() {
      return !isOldUI();
    },

    [FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_ARTICLE_AUTO_POPUP]() {
      return isAutoPopup();
    },

    [FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_MINIMISE_CATEGORY_HOT_QUESTION](displayType: NewHotQuestionDisplayType, source: number) {
      if (isDriverOrFood()) {
        return (source === IntentSource.CategoryHotQuestion || source === IntentSource.IntentClickOrder) && isDriverAndFoodUIOptimisation();
      }

      return displayType === NewHotQuestionDisplayType.Minimise;
    },
  },

  getFeatureEnable(featureName: keyof TogglesConfigs, ...args: any): boolean {
    const toggle = this._toggles[featureName];
    if (toggle && typeof toggle === 'function') {
      // driver and food app 新UI优化实验
      if (isDriverOrFood() && DRIVER_AND_FOOD_UI_OPTIMISATION_FEATURE_NAME_LIST.includes(featureName)) {
        const isUIOptimisation = isDriverAndFoodUIOptimisation();
        if (DRIVER_AND_FOOD_UI_OPTIMISATION_NEGATE_FEATURE_NAME_LIST.includes(featureName)) {
          return !isUIOptimisation;
        }

        return isUIOptimisation;
      }

      // @ts-ignore
      return toggle(...args);
    }
    return toggle;
  },
};

const DRIVER_AND_FOOD_UI_OPTIMISATION_FEATURE_NAME_LIST = [
  FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_HOT_QUESTION_CATEGORY,
  FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_INTENT_HOT_QUESTION,
  FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_PART_3_ACCORDION,
  FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_PART_3_BUTTON,
  FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_PART_3_TABLE,
  FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_ARTICLE,
  FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_HOT_QUESTION_CATEGORY_LANDING_PAGE,
  FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_ARTICLE_AUTO_POPUP,
];

const DRIVER_AND_FOOD_UI_OPTIMISATION_NEGATE_FEATURE_NAME_LIST = [
  FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_HOT_QUESTION_CATEGORY_LANDING_PAGE,
  FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_ARTICLE_AUTO_POPUP,
];
